import { format } from 'date-fns';
import { useFormik } from 'formik';
import { useEffect, useMemo, useRef, useState } from 'react';
import { MdErrorOutline } from 'react-icons/md';
import { toast } from 'react-toastify';
import useSWR, { mutate } from 'swr';
import * as Yup from 'yup';
import { consignmentService } from 'services';
import { useAuth } from 'hooks';
import { ConsignmentQuestion } from 'types';
import { Button, Heading, Loading, Text } from 'components';
import { formValidations, swrKeys } from 'helpers';
import { FontSize, FontWeight, QuestionType, deliveryModes } from 'commons';

export function DeliveryFailReport() {
  const { selectedStore } = useAuth();
  const brand = selectedStore.brand || '';
  const store = selectedStore.name || '';

  const {
    data: consignmentQuestionList,
    isLoading,
    isValidating,
  } = useSWR(swrKeys.consignmentQuestions({ brand, store }), () =>
    consignmentService.getDeliveryFailed({ store })
  );

  const [selectedConsignment, setSelectedConsignment] =
    useState<ConsignmentQuestion | null>(
      consignmentQuestionList ? consignmentQuestionList[0] : null
    );
  const [isSending, setIsSending] = useState(false);

  const firstSet = useRef(true);

  const deliveryMode = useMemo(
    () =>
      deliveryModes.find(
        (deliveryMode) =>
          selectedConsignment?.deliveryMode === deliveryMode.value
      ),
    [selectedConsignment]
  );

  useEffect(() => {
    if (firstSet) {
      if (!selectedConsignment && consignmentQuestionList) {
        setSelectedConsignment(consignmentQuestionList[0]);
        firstSet.current = false;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consignmentQuestionList]);

  const formValidation = Yup.object().shape({
    answer: Yup.string().required('Status do pedido'),
  });

  const { values, handleChange, handleSubmit, getFieldProps, errors } =
    useFormik({
      initialValues: { answer: null, observation: '' },

      validationSchema: formValidation,

      validate: (values) => {
        formValidations.validationSchema({
          schema: formValidation,
          values,
        });
      },

      validateOnBlur: false,
      validateOnChange: false,

      onSubmit: async (values) => {
        const { answer, observation } = values;

        if (answer && selectedConsignment) {
          setIsSending(true);

          const response = await consignmentService.postDeliveryFailed({
            consignmentCode: selectedConsignment.consignmentCode,
            answer,
            observation,
          });

          if (response?.status === 200) {
            const newQuestionList = await mutate(
              swrKeys.consignmentQuestions({ brand, store })
            );

            const newSelectedConsignment = newQuestionList[0];
            if (newSelectedConsignment) {
              setSelectedConsignment(newSelectedConsignment);
            }

            toast.success('Resposta enviada!');
          }

          setIsSending(false);
        }
      },
    });

  if (isLoading) {
    return (
      <div className="px-6 py-28">
        <Loading />
      </div>
    );
  }

  if (!consignmentQuestionList || !consignmentQuestionList.length) {
    return (
      <div className="px-6 py-28">
        <span>Nenhum pedido para verificar</span>
      </div>
    );
  }

  const missingAnswer = Boolean(errors.answer) && !values.answer;

  return (
    <div className="center flex h-[430px] flex-row gap-6 p-6">
      {consignmentQuestionList.length > 1 && (
        <div className="flex">
          <div className="relative flex flex-col gap-4 pr-3">
            {isValidating && (
              <div
                className="absolute left-0 top-0 flex h-full w-full items-center justify-center gap-2
                  bg-neutral-500/70 p-5 text-white"
              >
                <div className="w-8">
                  <Loading />
                </div>
              </div>
            )}

            <Heading size={FontSize.font12} weight={FontWeight.bold}>
              Pedidos a verificar
            </Heading>

            <div className="flex flex-col gap-4 overflow-y-auto overflow-x-hidden pr-3">
              {consignmentQuestionList.map((consignment) => {
                const formattedDate = format(
                  consignment.questionDate,
                  'dd/MM/yyyy'
                );

                const isSelected =
                  consignment.consignmentCode ===
                  selectedConsignment?.consignmentCode;

                return (
                  <button
                    key={consignment.consignmentCode}
                    className={`flex h-16 w-60 cursor-pointer flex-col justify-center gap-1 rounded-2xl border-2
                    border-solid border-neutral-300 bg-transparent p-4 ${ isSelected &&
                    'border-primary-300' }`}
                    type="button"
                    onClick={() => setSelectedConsignment(consignment)}
                  >
                    <Text size={FontSize.font12} weight={FontWeight.bold}>
                      Pedido {consignment.consignmentCode}
                    </Text>
                    <Text size={FontSize.font10}>
                      Solicitado em {formattedDate}
                    </Text>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="block h-full min-w-px bg-neutral-300" />
        </div>
      )}

      <div className="flex w-96 min-w-72 flex-col gap-6">
        <Heading size={FontSize.font16} weight={FontWeight.bold}>
          Pedido {selectedConsignment?.consignmentCode} está na loja?
        </Heading>

        <div className="flex flex-col gap-1 pb-2">
          <Text size={FontSize.font12}>
            <strong>Cliente</strong> {selectedConsignment?.customerName}
          </Text>

          {deliveryMode && (
            <Text size={FontSize.font12}>
              <strong>Transportadora</strong> {deliveryMode?.label}
            </Text>
          )}
        </div>

        <form
          onSubmit={handleSubmit}
          className="flex flex-1 flex-col justify-between gap-4 pb-3"
        >
          <div className="flex flex-col gap-4">
            <p className={`text-font12 ${missingAnswer && 'text-danger-300'}`}>
              O pedido está na loja? <span className="text-danger-300">*</span>
            </p>

            <div className="flex flex-col gap-4 pl-2 text-font14">
              <label className="flex flex-row items-center gap-2">
                <input
                  name="answer"
                  type="radio"
                  value={QuestionType.Store}
                  onChange={getFieldProps('answer').onChange}
                />
                Sim, o pedido está na loja
              </label>
              <label className="flex flex-row items-center gap-2">
                <input
                  name="answer"
                  type="radio"
                  value={QuestionType.Carrier}
                  onChange={getFieldProps('answer').onChange}
                />
                Não, o pedido está com a transportadora
              </label>
              <label className="flex flex-row items-center gap-2">
                <input
                  name="answer"
                  type="radio"
                  value={QuestionType.Transfer}
                  onChange={getFieldProps('answer').onChange}
                />
                Não, o pedido está em transferência
              </label>
            </div>

            {missingAnswer && (
              <div className="flex items-center gap-2 text-danger-300">
                <MdErrorOutline />
                <span className="text-font12">Resposta obrigatória</span>
              </div>
            )}

            <div className="pt-4">
              <label className="flex flex-col gap-1 text-font12">
                Observação
                <textarea
                  className="h-32 w-full resize-none rounded-lg border border-solid border-neutral-300 p-3
                    placeholder-neutral-300 placeholder:text-font12"
                  name="observation"
                  placeholder="Conte-nos sobre a situação do pedido"
                  value={values.observation}
                  onChange={handleChange}
                />
              </label>
            </div>
          </div>

          <Button
            full
            type="submit"
            isLoading={isSending || isLoading || isValidating}
          >
            Concluir verificação
          </Button>
        </form>
      </div>
    </div>
  );
}
